import apiClient, { getErrorMessage } from './client';

type ResultEnvelope<T> = {
  isSuccess: boolean;
  data?: T;
  error?: string | null;
};

export type ComplianceReferenceCategory =
  | 'PAYEE_RETURN_REASON'
  | 'GLOBAL_ACCOUNT_RETURN_REASON'
  | 'KYC_RETURN_REASON'
  | 'CLIENT_DECLINE_REASON'
  | 'RESTRICTION_REASON'
  | 'REQUISITES_RETURN_REASON'
  | 'INDUSTRY'
  | 'LEGAL_FORM'
  | 'APPROVAL_RULE';

export type PublicComplianceReferenceEntry = {
  code: string;
  clientText: string;
  parentCode?: string | null;
  requiresComment: boolean;
  sortOrder: number;
};

export type ComplianceReferenceEntry = PublicComplianceReferenceEntry & {
  id: string;
  category: ComplianceReferenceCategory;
  internalDescription?: string | null;
  applicableClientTypes: string[];
  isProhibited: boolean;
  requiresComplianceApproval: boolean;
  isActive: boolean;
  updatedAt: string;
};

export type UpsertComplianceReferenceEntry = {
  category: ComplianceReferenceCategory;
  code: string;
  clientText: string;
  internalDescription?: string | null;
  parentCode?: string | null;
  applicableClientTypes: string[];
  isProhibited: boolean;
  requiresComplianceApproval: boolean;
  requiresComment: boolean;
  isActive: boolean;
  sortOrder: number;
};

function unwrap<T>(data: unknown): T {
  if (data !== null && typeof data === 'object' && 'isSuccess' in data) {
    const result = data as ResultEnvelope<T>;
    if (!result.isSuccess) throw new Error(result.error || 'Request failed.');
    if (result.data === undefined) throw new Error('Empty response data.');
    return result.data;
  }
  return data as T;
}

export const complianceReferenceApi = {
  list: async (category: ComplianceReferenceCategory, clientType?: string): Promise<PublicComplianceReferenceEntry[]> => {
    try {
      const response = await apiClient.get('/ComplianceReference', { params: { category, clientType } });
      return unwrap<PublicComplianceReferenceEntry[]>(response.data);
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  listAdmin: async (category?: ComplianceReferenceCategory): Promise<ComplianceReferenceEntry[]> => {
    try {
      const response = await apiClient.get('/ComplianceReference/admin', { params: { category } });
      return unwrap<ComplianceReferenceEntry[]>(response.data);
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  create: async (payload: UpsertComplianceReferenceEntry): Promise<ComplianceReferenceEntry> => {
    try {
      const response = await apiClient.post('/ComplianceReference/admin', payload);
      return unwrap<ComplianceReferenceEntry>(response.data);
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },

  update: async (id: string, payload: UpsertComplianceReferenceEntry): Promise<ComplianceReferenceEntry> => {
    try {
      const response = await apiClient.put(`/ComplianceReference/admin/${id}`, payload);
      return unwrap<ComplianceReferenceEntry>(response.data);
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error));
    }
  },
};
